import React, { useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";
import FormErrorMessage from "../../components/FormErrorMessage";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { UserType } from "../../services/user.service";

interface RichTextEditorProps {
  value: string;
  onChange: (newValue: string) => void;
  errorMsg?: string;
  errorInitiated?: boolean;
  height?: number;
  readOnlyFallback?: boolean; // if true, shows rendered HTML for non-admins
  showFullscreen?: boolean; // if false, hides fullscreen option
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  errorMsg = "This field is required",
  errorInitiated = false,
  height = 400,
  readOnlyFallback = true,
  showFullscreen = true,
}) => {
  const user = useSelector((state: RootState) => state.currentUser.currentUser);
  const isAdmin = user?.userType === UserType.SystemAdmin;
  const editorRef = useRef<any>(null);

  const plugins = ["lists", "link", "image", "table", "code", "preview"];
  if (showFullscreen) {
    plugins.push("fullscreen");
  }

  const toolbarBase = "undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link image | table | code preview";
  const toolbar = showFullscreen ? toolbarBase + " fullscreen" : toolbarBase;

  return (
    <>
      {isAdmin ? (
        <div id="editorContent">
          <Editor
            onInit={(_, editor) => (editorRef.current = editor)}
            value={value}
            onEditorChange={onChange}
            apiKey="fpry4n9gj4ptb1uppl4mztgkqzh8g1j1wlcd70x0e8qoczll"
            init={{
              height: height,
              menubar: true,
              branding: false,
              licenseKey: "gpl",
              promotion: false,
              plugins: plugins,
              toolbar: toolbar,
              content_style: `
              table {
                border-collapse: unset !important;
              }
            `,
              forced_root_block: false as any,
              cleanup: false,
              verify_html: false,
              valid_elements: "*[*]",
              extended_valid_elements: "script[language|type|src],style[type|media]",
              valid_children: "+body[style],+body[script]",
              convert_urls: false,
              remove_script_host: false,
              entity_encoding: "raw",
            }}
          />
          {errorInitiated && !value && <FormErrorMessage text={errorMsg} />}
        </div>
      ) : readOnlyFallback ? (
        <div className="p-4 border rounded bg-gray-50 prose max-w-full" dangerouslySetInnerHTML={{ __html: value }} />
      ) : null}
    </>
  );
};

export default RichTextEditor;
